import { AlertTriangle, CheckCircle2, Mail } from 'lucide-react';
import { cn } from '../../lib/cn';
import { linkAcquirente, type OrdineConBiglietti } from '../../lib/eventi/biglietti';
import {
  ordiniDaSentire,
  postaDellOrdine,
  statoEmailAcquirente,
  type OrdineDaSentire,
  type PostaAcquirente,
  type StatoEmailAcquirente,
} from '../../lib/eventi/daSentire';
import { dataIt, dataOraIt } from '../../lib/eventi/stati';
import type { Evento } from '../../lib/eventi/tipi';
import { CopiaLink } from './CopiaLink';
import { Pannello } from './Pannello';

const TITOLO_LINK = 'Copia il link della pagina dei biglietti di chi ha pagato (quella dell\'email "registra i partecipanti")';

/** L'email per registrare, in una frase, con il colore che dice se va bene. */
function descriviEmail(email: StatoEmailAcquirente): { testo: string; classe: string } {
  switch (email.stato) {
    case 'nessuna':
      return { testo: 'non ancora partita', classe: 'text-amber-700' };
    case 'in_coda':
      return { testo: `parte il ${dataOraIt(email.quando)}`, classe: 'text-neutral-600' };
    case 'in_viaggio':
      return { testo: `partita il ${dataOraIt(email.quando)}, in consegna`, classe: 'text-neutral-600' };
    case 'ferma':
      return { testo: `partita il ${dataOraIt(email.dal)}, non ancora arrivata`, classe: 'text-red-700' };
    case 'rifiutata':
      return { testo: `rifiutata dal server di posta${email.errore ? ` (${email.errore})` : ''}`, classe: 'text-red-700' };
    case 'consegnata':
      return {
        testo: `consegnata il ${dataOraIt(email.quando)} · ${email.cliccata ? 'link aperto' : 'link mai aperto'}`,
        classe: email.cliccata ? 'text-green-700' : 'text-neutral-600',
      };
  }
}

const biglietti = (n: number) => (n === 1 ? '1 biglietto da registrare' : `${n} biglietti da registrare`);

/** Perche' sentirlo, in parole semplici, e cosa fare. */
function descriviMotivo(d: OrdineDaSentire): { etichetta: string; testo: string; classe: string } {
  const rosso = 'border-red-200 bg-red-50 text-red-700';
  const giallo = 'border-amber-200 bg-amber-50 text-amber-800';
  const email = d.email;
  switch (d.motivo) {
    case 'email_rifiutata':
      return {
        etichetta: 'Email rifiutata',
        testo: `Il server di posta ha rifiutato l'email${email.stato === 'rifiutata' && email.errore ? ` (${email.errore})` : ''}. Controlla l'indirizzo e manda il link per un'altra strada.`,
        classe: rosso,
      };
    case 'email_ferma':
      return {
        etichetta: 'Email non arrivata',
        testo: `Partita il ${dataOraIt(email.stato === 'ferma' ? email.dal : null)}, ma il server di posta di chi ha pagato non l'ha ancora accettata. Manda il link per un'altra strada.`,
        classe: rosso,
      };
    case 'email_mancante':
      return {
        etichetta: 'Email non partita',
        testo: d.token
          ? "L'email per registrare non è ancora partita: manda tu il link."
          : 'Pixel non ha ancora collegato chi ha pagato, quindi niente email e niente link. Prova «Leggi adesso».',
        classe: giallo,
      };
    case 'non_registrati':
      return {
        etichetta: 'Non registrato',
        testo: `Pagato il ${dataIt(d.ordine.ordine.pagato_il ?? d.ordine.ordine.creato_il)}, ancora ${biglietti(d.daRegistrare)}. Email ${descriviEmail(email).testo}${email.stato === 'consegnata' && !email.cliccata ? ': forse è finita nello spam' : ''}.`,
        classe: giallo,
      };
  }
}

/** Nella carta di un ordine: com'e' andata l'email per registrare, e il link da copiare. */
export function RigaEmailOrdine({
  evento,
  ordine,
  posta,
}: {
  evento: Evento;
  ordine: OrdineConBiglietti;
  posta: Map<string, PostaAcquirente>;
}) {
  if (ordine.biglietti.every((b) => b.stato === 'annullato')) return null;
  const suaPosta = postaDellOrdine(ordine, posta);
  const email = descriviEmail(statoEmailAcquirente(suaPosta.email, new Date()));
  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
      <span className="inline-flex min-w-0 items-center gap-1.5 text-neutral-500">
        <Mail className="h-3.5 w-3.5 flex-shrink-0" />
        Email per registrare: <span className={cn('font-medium', email.classe)}>{email.testo}</span>
      </span>
      {suaPosta.token && (
        <CopiaLink link={linkAcquirente(evento.url_registrazione, suaPosta.token, window.location.origin)} titolo={TITOLO_LINK} />
      )}
    </div>
  );
}

type Props = {
  evento: Evento;
  ordini: OrdineConBiglietti[];
  /** Null mentre si legge. */
  posta: Map<string, PostaAcquirente> | null;
  errore: string | null;
};

/**
 * Chi chiamare: l'email per registrare non e' arrivata, o e' arrivata e dopo
 * tre giorni i biglietti non sono ancora registrati.
 */
export function DaSentire({ evento, ordini, posta, errore }: Props) {
  if (errore) {
    return (
      <Pannello>
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>Non riesco a leggere le email mandate a chi ha pagato: {errore}</p>
        </div>
      </Pannello>
    );
  }
  if (!posta) return null;

  const elenco = ordiniDaSentire(ordini, posta, new Date());
  if (elenco.length === 0) {
    return (
      <Pannello>
        <p className="flex items-center gap-2 text-sm text-neutral-600">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />
          Nessuno da sentire: le email per registrare sono arrivate, e chi ha pagato da più di 3 giorni ha registrato.
        </p>
      </Pannello>
    );
  }

  return (
    <Pannello
      titolo={`Da sentire (${elenco.length})`}
      sotto="Chi ha pagato ma non ha ricevuto l'email per registrare i biglietti, o dopo 3 giorni non ha ancora registrato. «Copia link» copia la sua pagina dei biglietti: mandala per WhatsApp o leggila al telefono."
      corpo="p-0"
    >
      <ul className="divide-y divide-gray-100">
        {elenco.map((d) => {
          const motivo = descriviMotivo(d);
          const o = d.ordine.ordine;
          return (
            <li key={o.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:gap-4 md:px-5">
              <span className={cn('w-fit flex-shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold sm:w-36 sm:text-center', motivo.classe)}>
                {motivo.etichetta}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">
                  <span className="font-medium text-gray-900">{o.acquirente}</span>
                  <span className="ml-2 text-xs text-neutral-500">
                    {[o.email !== o.acquirente ? o.email : null, `ordine n. ${o.numero}`].filter(Boolean).join(' · ')}
                  </span>
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-neutral-600">{motivo.testo}</p>
              </div>
              {d.token && (
                <span className="flex-shrink-0">
                  <CopiaLink link={linkAcquirente(evento.url_registrazione, d.token, window.location.origin)} titolo={TITOLO_LINK} />
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </Pannello>
  );
}
